import React, { useState } from "react";
import { Button, CardContent, Typography, Divider, CircularProgress } from "@material-ui/core";
import { compose } from "redux";
import { Field, reduxForm, change } from "redux-form";
import { connect, useDispatch } from "react-redux";
import { country } from "./components/Terms";
import CustomInputForm from "../custom/CustomInput";
import customSelect from "../custom/CustomSelect";
import HeaderPageItem from "../custom/HeaderPageItem";
import PageContainer, { CardPageItem, PageItem } from "../custom/Page";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl";
import { createSecondaryUser } from "../actions/User";
import { asyncValidateRegister } from "../util";
import UserAccountList from "./components/UserAccountList";
import { Link } from "react-router-dom";
import br from "../translations/br.json";
import en from "../translations/en.json";
import es from "../translations/es.json";

const AgentFormCustomer = ({ currentUser, handleSubmit, pristine, invalid, submitting }) => {
  const dispatch = useDispatch();
  const [loadingState, setLoadingState] = useState("idle");

  const onSubmit = async (formElements) => {
    try {
      setLoadingState("submitting");
      await dispatch(createSecondaryUser(formElements));
      setTimeout(() => setLoadingState("done"), 200);
    } catch (e) {
      setLoadingState("idle");
    }
  };

  const handleTaxResidence = (event) => {
    const { locale } = currentUser;
    const { value } = event.target;
    const translations = {
      br: br,
      en: en,
      es: es,
    };

    const country = translations?.[locale]?.[`app.taxResidence.${value}`] ?? translations?.["br"]?.[`app.taxResidence.${value}`] ?? "secondary";
    const countryWithoutAccent = country.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const countryFormatted = countryWithoutAccent.replace(" ", "");
    const [user, domain] = currentUser.email.split("@");

    const newName = `${currentUser.name} - ${country}`;
    const newEmail = `${user}+${countryFormatted}@${domain}`.toLocaleLowerCase();

    dispatch(change("registerSecondaryUser", "name", newName));
    dispatch(change("registerSecondaryUser", "email", newEmail));
  };

  if (!currentUser.apt) {
    return (
      <PageContainer>
        <PageItem>
          <HeaderPageItem title="app.user.secondary" />
        </PageItem>
        <CardPageItem raised>
          <CardContent>
            <Typography>
              <FormattedHTMLMessage id="app.dashboard.user.pending" />
            </Typography>
          </CardContent>
        </CardPageItem>
      </PageContainer>
    );
  }

  if (currentUser.isSecondary) {
    return (
      <PageContainer>
        <PageItem>
          <HeaderPageItem title="app.user.secondary" />
        </PageItem>
        <CardPageItem raised>
          <CardContent>
            <Typography gutterBottom variant="subtitle1">
              <FormattedHTMLMessage id="app.user.secondary.conflict" />
            </Typography>
            {currentUser?.primaryUserId?.email && (
              <Typography gutterBottom variant="body1">
                <Typography color="primary" component={"span"}>
                  <FormattedMessage id="app.email" /> {": "}
                </Typography>
                {currentUser.primaryUserId.email}
              </Typography>
            )}
          </CardContent>
        </CardPageItem>
      </PageContainer>
    );
  }

  if (loadingState === "submitting") {
    return (
      <PageContainer>
        <HeaderPageItem title="app.user.secondary" showBackButton destination="/more" />
        <PageItem>
          <CardPageItem>
            <CardContent>
              <Typography gutterBottom align="center" variant="h6" color="primary">
                <div style={{ textAlign: "center" }}>
                  <CircularProgress style={{ marginTop: "25%" }} />
                </div>
              </Typography>
            </CardContent>
          </CardPageItem>
        </PageItem>
      </PageContainer>
    );
  }

  if (loadingState === "done") {
    return (
      <PageContainer>
        <HeaderPageItem title="app.user.secondary" showBackButton destination="/more" />
        <CardPageItem raised>
          <CardContent>
            <Typography gutterBottom variant="h6" color="primary">
              <FormattedMessage id={"app.user.secondary.registered"} />
              <Divider />
            </Typography>
            <Typography gutterBottom variant="body1">
              <FormattedHTMLMessage id="app.user.secondary.alert" />
            </Typography>
            <Button component={Link} fullWidth style={{ marginTop: 10 }} variant="contained" color="primary" size="large" to="/dashboard">
              <FormattedMessage id="app.ok" />
            </Button>
          </CardContent>
        </CardPageItem>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderPageItem title="app.user.secondary" showBackButton destination="/more" />
      <PageItem>
        <CardPageItem raised>
          <CardContent>
            <Typography gutterBottom variant="h6" color="primary">
              <FormattedMessage id={"app.user.secondary.selectTax"} />
              <Divider />
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Field component={customSelect} InputLabelProps={{ shrink: true }} label="app.taxResidence" name="taxResidence" onChange={handleTaxResidence} values={country} />
              <Field
                component={CustomInputForm}
                label="app.name"
                InputLabelProps={{
                  shrink: true,
                }}
                name="name"
              />
              <Field
                component={CustomInputForm}
                label="app.email"
                name="email"
                InputLabelProps={{
                  shrink: true,
                }}
                type="email"
              />
              <Typography gutterBottom variant="body1">
                <FormattedHTMLMessage id="app.user.secondary.alert" />
              </Typography>
              <Button fullWidth style={{ marginTop: 10 }} variant="contained" color="primary" size="large" type="submit" disabled={pristine || invalid || submitting}>
                <FormattedMessage id="app.signup" />
              </Button>
            </form>
          </CardContent>
        </CardPageItem>
        <br />
        <CardPageItem>
          <UserAccountList user={currentUser} defaultExpanded={true} showMainAccount={true} title="app.user.secondary.yourAccounts" />
        </CardPageItem>
      </PageItem>
    </PageContainer>
  );
};

const mapStateToProps = (state) => {
  return { currentUser: state.user.user.me };
};

export default compose(
  connect(mapStateToProps),
  reduxForm({
    asyncBlurFields: ["email", "taxResidence"],
    asyncValidateRegister,
    form: "registerSecondaryUser",
    touchOnChange: true,
    touchOnBlur: true,
  })
)(AgentFormCustomer);
